/**
 * StarNexus Service Factory
 * @module services/starnexus
 *
 * Uses real API when VITE_USE_REAL_API=true, otherwise mock.
 * Set in .env or .env.local: VITE_USE_REAL_API=true
 */

import {
  mockDatasetService,
  mockProcessingService,
  mockTeamService,
  mockMetricsService,
  mockAndesAccessService,
} from './mock';
import { realDatasetService } from './real/realDatasetService';
import { realProcessingService } from './real/realProcessingService';
import { realTeamService } from './real/realTeamService';
import { realMetricsService } from './real/realMetricsService';
import { realAndesAccessService } from './real/realAndesAccessService';

const useRealApi = import.meta.env.VITE_USE_REAL_API === 'true';

// Service types for type safety
export type DatasetService = typeof mockDatasetService;
export type ProcessingService = typeof mockProcessingService;
export type TeamService = typeof mockTeamService;
export type MetricsService = typeof mockMetricsService;
export type AndesAccessService = typeof mockAndesAccessService;

// Direct service selection: real API or mock, no silent fallback.
// This ensures local dev behaves identically to the deployed version.
export const datasetService = useRealApi ? (realDatasetService as unknown as typeof mockDatasetService) : mockDatasetService;
export const processingService = useRealApi ? (realProcessingService as unknown as typeof mockProcessingService) : mockProcessingService;
export const teamService = useRealApi ? (realTeamService as unknown as typeof mockTeamService) : mockTeamService;
export const metricsService = useRealApi ? (realMetricsService as unknown as typeof mockMetricsService) : mockMetricsService;
export const andesAccessService = useRealApi ? (realAndesAccessService as unknown as typeof mockAndesAccessService) : mockAndesAccessService;

export const services = {
  dataset: datasetService,
  processing: processingService,
  team: teamService,
  metrics: metricsService,
  andesAccess: andesAccessService,
};

// Reset all services (useful for testing)
export const resetAllServices = (): void => {
  if (!useRealApi) {
    mockDatasetService.reset();
    mockProcessingService.reset();
    mockTeamService.reset();
  }
};

// Re-export mock services for direct access if needed
export * from './mock';
